/**
 * EditStyleModal Component
 * Modal for editing tone and formality settings.
 */
import React, { useState, useEffect } from 'react';

interface EditStyleModalProps {
  isOpen: boolean;
  tone: string | null;
  formality: string | null;
  onSave: (tone: string, formality: string) => void;
  onClose: () => void;
}

const TONE_OPTIONS = [
  'encouraging',
  'encouraging but direct',
  'neutral',
  'direct',
  'critical but constructive',
  'formal and objective',
];

const FORMALITY_OPTIONS = [
  'casual',
  'semi-formal',
  'formal',
  'academic',
];

const EditStyleModal: React.FC<EditStyleModalProps> = ({
  isOpen,
  tone,
  formality,
  onSave,
  onClose,
}) => {
  const [selectedTone, setSelectedTone] = useState(tone || 'neutral');
  const [selectedFormality, setSelectedFormality] = useState(
    formality || 'semi-formal'
  );

  // Reset values when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedTone(tone || 'neutral');
      setSelectedFormality(formality || 'semi-formal');
    }
  }, [isOpen, tone, formality]);

  const handleSave = () => {
    onSave(selectedTone, selectedFormality);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Edit Style Settings
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Tone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tone
            </label>
            <select
              value={selectedTone}
              onChange={(e) => setSelectedTone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {TONE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              The overall tone of your feedback comments
            </p>
          </div>

          {/* Formality */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Formality
            </label>
            <select
              value={selectedFormality}
              onChange={(e) => setSelectedFormality(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {FORMALITY_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              The formality level of your language
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditStyleModal;
